import React from "react";
import { Sparkles, CheckCircle2, AlertTriangle, Wrench, Clock, Lightbulb } from "lucide-react";
import { Badge } from "./ui/badge";

export const AIAdvisor = ({ advice, loading }) => {
  if (!advice && !loading) return null;

  return (
    <div
      data-testid="ai-mechanic-advisor"
      className="surface-card animate-slide-up relative overflow-hidden rounded-2xl p-6 sm:p-8"
    >
      <div className="pointer-events-none absolute -right-20 -top-24 h-64 w-64 rounded-full bg-amber-500/10 blur-3xl" />

      <div className="relative">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-amber-500/20 to-amber-700/10 ring-1 ring-amber-500/30">
            <Sparkles className="h-5 w-5 text-amber-300" />
          </div>
          <div>
            <span className="font-mono text-[11px] uppercase tracking-widest text-amber-400">
              Mécanicien IA
            </span>
            <h3 className="mt-0.5 font-heading text-xl font-bold text-slate-50">
              Conseil personnalisé
            </h3>
          </div>
        </div>

        {loading && (
          <div className="mt-5 space-y-3">
            <div className="h-4 w-5/6 rounded shimmer" />
            <div className="h-4 w-3/4 rounded shimmer" />
            <div className="h-4 w-2/3 rounded shimmer" />
            <div className="mt-6 h-20 rounded shimmer" />
          </div>
        )}

        {advice && (
          <div className="mt-5 space-y-5">
            <p
              data-testid="ai-advice-resume"
              className="text-base leading-relaxed text-slate-200"
            >
              {advice.resume}
            </p>

            <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-4">
              <div className="mb-1 flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-emerald-400">
                <CheckCircle2 className="h-3.5 w-3.5" />
                Compatibilité véhicule
              </div>
              <p className="text-sm text-slate-200">{advice.compatibilite}</p>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              {advice.points_cles?.length > 0 && (
                <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-4">
                  <div className="mb-2 flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-cyan-400">
                    <Lightbulb className="h-3.5 w-3.5" />
                    Points clés avant achat
                  </div>
                  <ul className="space-y-2 text-sm text-slate-300">
                    {advice.points_cles.map((p, i) => (
                      <li key={i} className="flex gap-2">
                        <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-cyan-400" />
                        {p}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {advice.signes_usure?.length > 0 && (
                <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-4">
                  <div className="mb-2 flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-amber-400">
                    <AlertTriangle className="h-3.5 w-3.5" />
                    Signes d'usure typiques
                  </div>
                  <ul className="space-y-2 text-sm text-slate-300">
                    {advice.signes_usure.map((s, i) => (
                      <li key={i} className="flex gap-2">
                        <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-400" />
                        {s}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="flex flex-wrap gap-3">
              <Badge
                variant="outline"
                className="border-slate-700 bg-slate-900/70 py-2 text-xs text-slate-300"
              >
                <Wrench className="mr-1.5 h-3.5 w-3.5 text-cyan-400" />
                Difficulté : {advice.difficulte_pose}
              </Badge>
              <Badge
                variant="outline"
                className="border-slate-700 bg-slate-900/70 py-2 text-xs text-slate-300"
              >
                <Clock className="mr-1.5 h-3.5 w-3.5 text-cyan-400" />
                Temps estimé : {advice.temps_pose_estime}
              </Badge>
            </div>

            {advice.conseil_pro && (
              <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-4">
                <div className="mb-1 font-mono text-[11px] uppercase tracking-widest text-amber-400">
                  Conseil du pro
                </div>
                <p className="text-sm italic text-slate-200">{advice.conseil_pro}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
